import { fetchJSON as fetchAPI } from './client';

export type ContentDashboard = {
  tenant_id: string;
  total_assets: number;
  avg_content_score: number;
  avg_conversion_readiness: number;
  low_score_assets: number;
  a_plus_coverage_pct: number;
  brand_store_score: number;
  active_experiments: number;
  active_recommendations: number;
  avg_ctr: number;
  avg_conversion_rate: number;
};

export type ContentAsset = {
  id: string;
  sku: string;
  asin?: string;
  title?: string;
  asset_type: string;
  completeness_pct: number;
  seo_score: number;
  brand_consistency: number;
  readability: number;
  conversion_readiness: number;
};

export type ContentScore = {
  id: string;
  sku: string;
  asin?: string;
  overall_score: number;
  seo_quality: number;
  brand_consistency: number;
  visual_consistency: number;
  readability: number;
  completeness: number;
  compliance: number;
  customer_clarity: number;
  conversion_readiness: number;
};

export type APlusRecord = {
  id: string;
  sku: string;
  asin?: string;
  module_count: number;
  recommended_modules?: string[];
  layout_score: number;
  visual_story_score: number;
  comparison_present: boolean;
  lifestyle_present: boolean;
};

export type ImageIntel = {
  id: string;
  sku: string;
  asin?: string;
  image_count: number;
  hero_score: number;
  lifestyle_score: number;
  infographic_score: number;
  recommended_order?: string[];
  issues?: string[];
};

export type BrandStoreRecord = {
  id: string;
  store_name: string;
  navigation_score: number;
  collection_score: number;
  landing_score: number;
  cross_sell_score: number;
  brand_messaging_score: number;
  overall_score: number;
  issues?: string[];
};

export type ExperimentRecord = {
  id: string;
  name: string;
  type: string;
  sku?: string;
  status?: string;
  variant_a?: string;
  variant_b?: string;
  ctr_impact?: number;
  conversion_impact?: number;
  winner?: string;
  created_at: string;
};

export type PerformanceMetric = {
  id: string;
  sku: string;
  asin?: string;
  ctr: number;
  conversion_rate: number;
  engagement_score: number;
  business_impact_score: number;
  period?: string;
};

export type ContentRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  evidence?: string;
  sku?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
};

const API_BASE = import.meta.env.VITE_CONTENTINTEL_API ?? '/api/v1/contentintel';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const contentintelApi = {
  dashboard: () => fetchJSON<ContentDashboard>('/dashboard'),
  listContent: () => fetchJSON<ContentAsset[]>('/content'),
  listScores: () => fetchJSON<ContentScore[]>('/scores'),
  listAPlus: () => fetchJSON<APlusRecord[]>('/aplus'),
  listImages: () => fetchJSON<ImageIntel[]>('/images'),
  listBrandStore: () => fetchJSON<BrandStoreRecord[]>('/brand-store'),
  listPerformance: () => fetchJSON<PerformanceMetric[]>('/performance'),
  listExperiments: () => fetchJSON<ExperimentRecord[]>('/experiments'),
  createExperiment: (body: { name: string; type: string; sku?: string; variant_a?: string; variant_b?: string }) =>
    fetchJSON<ExperimentRecord>('/experiments', { method: 'POST', body: JSON.stringify(body) }),
  listRecommendations: () => fetchJSON<ContentRecommendation[]>('/recommendations'),
  generateRecommendations: () => fetchJSON<ContentRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  approveRecommendation: (id: string) =>
    fetchJSON<ContentRecommendation>(`/recommendations/${id}/approve`, { method: 'POST', body: JSON.stringify({}) }),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
